import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { Snackbar } from '@material-ui/core';
import JSZip from 'jszip';

import { getUserId } from '../../helpers/common';
import * as userStationsReducers from '../../system/reducer/UserStationsReducers';
import * as api from '../api';
import DownloadReportPortfolioSelect from './DownloadReportPortfolioSelect';
import DownloadReportPortfolioTable from './DownloadReportPortfolioTable';

class DownloadReportPortfolio extends Component {
  state = {
    selectedStationName: 'All',
    selectedYear: '',
    selectedMonth: '',
    selectedDay: '',
    years: [],
    months: [],
    days: [],
    files: [],
    message: '',
    isLoadingYears: false,
    isLoadingMonths: false,
    isLoadingDays: false,
    isSubmitting: false,
    isDownloading: false,
    hasSubmittedRequest: false,
  };

  availabilityRequestId = 0;
  listRequestId = 0;
  componentIsMounted = false;

  componentDidMount() {
    this.componentIsMounted = true;
    if (this.props.stations.length > 0) {
      this.loadAvailability('All');
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.stations.length === 0 && this.props.stations.length > 0) {
      this.loadAvailability(this.state.selectedStationName || 'All');
    }
  }

  componentWillUnmount() {
    this.componentIsMounted = false;
    this.availabilityRequestId += 1;
    this.listRequestId += 1;
  }

  getStationNames = stationName => {
    if (stationName === 'All') {
      return this.props.stations.map(station => station.name);
    }
    return [stationName];
  };

  isPortfolioRequest = stationName => stationName === 'All';

  getReportType = () => this.props.reportType || 'monthly';

  supportsDaySelection = () => {
    const reportType = this.getReportType();
    return reportType === 'hourly' || reportType === 'half-hourly';
  };

  getReportApis = isPortfolio => {
    switch (this.getReportType()) {
      case 'hourly':
        return {
          check: isPortfolio
            ? api.checkPortfolioDownloadHourlyObject
            : api.checkDownloadHourlyObject,
          list: isPortfolio
            ? api.listPortfolioDownloadHourlyObject
            : api.listDownloadHourlyObject,
          generate: isPortfolio
            ? api.generatePortfolioDownloadHourlyLink
            : api.generateDownloadHourlyLink,
          zipLabel: 'Hourly_Report',
        };
      case 'half-hourly':
        return {
          check: isPortfolio
            ? api.checkPortfolioDownloadHalfHourlyObject
            : api.checkDownloadHalfHourlyObject,
          list: isPortfolio
            ? api.listPortfolioDownloadHalfHourlyObject
            : api.listDownloadHalfHourlyObject,
          generate: isPortfolio
            ? api.generatePortfolioDownloadHalfHourlyLink
            : api.generateDownloadHalfHourlyLink,
          zipLabel: 'Half_Hourly_Report',
        };
      default:
        return {
          check: isPortfolio
            ? api.checkPortfolioDownloadReportObject
            : api.checkDownloadReportObject,
          list: isPortfolio
            ? api.listPortfolioDownloadReportObject
            : api.listDownloadReportObject,
          generate: isPortfolio
            ? api.generatePortfolioDownloadReportLink
            : api.generateDownloadReportLink,
          zipLabel: 'Download_Report',
        };
    }
  };

  showMessage = message => {
    if (this.componentIsMounted) {
      this.setState({ message: message || 'Unable to complete the request.' });
    }
  };

  loadAvailability = (stationName, year, month) => {
    if (!stationName) return Promise.resolve();

    const requestId = ++this.availabilityRequestId;
    const loadingState = month
      ? { isLoadingDays: true }
      : year
        ? { isLoadingMonths: true, isLoadingDays: false }
        : { isLoadingYears: true, isLoadingMonths: false, isLoadingDays: false };
    this.setState(loadingState);

    const isPortfolio = this.isPortfolioRequest(stationName);
    const request = {
      userId: getUserId(),
      stationName: isPortfolio ? this.getStationNames(stationName) : stationName,
    };
    if (year) request.year = String(year);
    if (month) request.month = month;

    const { check } = this.getReportApis(isPortfolio);

    return check(request)
      .then(response => {
        if (!this.componentIsMounted || requestId !== this.availabilityRequestId) return;
        if (response.status !== 200) {
          this.showMessage(response.message);
          return;
        }
        const data = response.data || {};
        this.setState({
          years: data.year || [],
          months: year ? (data.month || []) : [],
          days: month ? (data.day || []) : [],
        });
      })
      .catch(error => {
        if (requestId === this.availabilityRequestId) this.showMessage(error.message);
      })
      .then(() => {
        if (!this.componentIsMounted || requestId !== this.availabilityRequestId) return;
        if (month) {
          this.setState({ isLoadingDays: false });
        } else if (year) {
          this.setState({ isLoadingMonths: false });
        } else {
          this.setState({ isLoadingYears: false });
        }
      });
  };

  handleStationChange = selectedStationName => {
    this.listRequestId += 1;
    this.setState({
      selectedStationName,
      selectedYear: '',
      selectedMonth: '',
      selectedDay: '',
      years: [],
      months: [],
      days: [],
      files: [],
      isSubmitting: false,
      hasSubmittedRequest: false,
    });
    this.loadAvailability(selectedStationName);
  };

  handleYearChange = selectedYear => {
    this.listRequestId += 1;
    this.setState({
      selectedYear,
      selectedMonth: '',
      selectedDay: '',
      months: [],
      days: [],
      files: [],
      isSubmitting: false,
      hasSubmittedRequest: false,
    });
    this.loadAvailability(this.state.selectedStationName, selectedYear);
  };

  handleMonthChange = selectedMonth => {
    if (!selectedMonth) this.availabilityRequestId += 1;
    this.listRequestId += 1;
    this.setState({
      selectedMonth,
      selectedDay: '',
      days: [],
      files: [],
      isSubmitting: false,
      hasSubmittedRequest: false,
    });
    if (this.supportsDaySelection() && selectedMonth) {
      this.loadAvailability(this.state.selectedStationName, this.state.selectedYear, selectedMonth);
    } else if (!selectedMonth) {
      this.setState({ isLoadingDays: false });
    }
  };

  handleDayChange = selectedDay => {
    this.listRequestId += 1;
    this.setState({
      selectedDay,
      files: [],
      isSubmitting: false,
      hasSubmittedRequest: false,
    });
  };

  handleReset = () => {
    this.availabilityRequestId += 1;
    this.listRequestId += 1;
    this.setState({
      selectedStationName: '',
      selectedYear: '',
      selectedMonth: '',
      selectedDay: '',
      years: [],
      months: [],
      days: [],
      files: [],
      isLoadingYears: false,
      isLoadingMonths: false,
      isLoadingDays: false,
      isSubmitting: false,
      hasSubmittedRequest: false,
    });
  };

  handleSubmit = () => {
    const { selectedStationName, selectedYear, selectedMonth, selectedDay } = this.state;
    const isPortfolio = this.isPortfolioRequest(selectedStationName);
    if (!selectedStationName || !selectedYear || (isPortfolio && !selectedMonth)) return;

    const request = {
      userId: getUserId(),
      stationName: isPortfolio ? this.getStationNames(selectedStationName) : selectedStationName,
      year: String(selectedYear),
    };
    if (selectedMonth) request.month = selectedMonth;
    if (selectedDay) request.day = selectedDay;

    const { list } = this.getReportApis(isPortfolio);

    const requestId = ++this.listRequestId;
    this.setState({ isSubmitting: true, files: [], hasSubmittedRequest: true });
    list(request)
      .then(response => {
        if (!this.componentIsMounted || requestId !== this.listRequestId) return;
        if (response.status !== 200) {
          this.showMessage(response.message);
          return;
        }
        this.setState({ files: (response.data && response.data.files) || [] });
      })
      .catch(error => {
        if (requestId === this.listRequestId) this.showMessage(error.message);
      })
      .then(() => {
        if (this.componentIsMounted && requestId === this.listRequestId) {
          this.setState({ isSubmitting: false });
        }
      });
  };

  extractFileName = file => {
    const path = typeof file === 'string'
      ? file
      : (file.fileName || file.path || file.key || '');
    const parts = path.split('/');
    return parts[parts.length - 1];
  };

  downloadLinksAsZip = (links, selectedFiles, downloadContext) => {
    const selectedNames = selectedFiles.map(this.extractFileName);
    const selectedNameSet = new Set(selectedNames);
    const filteredLinks = links.filter(link => selectedNameSet.has(link.fileName));

    if (filteredLinks.length === 0) {
      this.showMessage('No download links were returned for the selected reports.');
      return Promise.resolve();
    }

    const zip = new JSZip();
    return Promise.all(filteredLinks.map(fileObject =>
      fetch(fileObject.downloadDataLink)
        .then(response => {
          if (!response.ok) throw new Error(`Unable to download ${fileObject.fileName}`);
          return response.blob();
        })
        .then(blob => zip.file(fileObject.fileName, blob))
    ))
      .then(() => zip.generateAsync({ type: 'blob' }))
      .then(content => {
        const {
          selectedStationName, selectedYear, selectedMonth, selectedDay,
        } = downloadContext;
        const stationLabel = selectedStationName === 'All' ? 'Portfolio' : selectedStationName;
        const periodLabel = selectedDay || selectedMonth || selectedYear;
        const { zipLabel: reportLabel } = this.getReportApis(
          this.isPortfolioRequest(selectedStationName)
        );
        const zipFileName = `SERIS_${reportLabel}_${stationLabel}_${periodLabel}.zip`;
        const url = window.URL.createObjectURL(content);
        const link = document.createElement('a');
        link.style.display = 'none';
        link.href = url;
        link.download = zipFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      });
  };

  handleDownload = selectedFiles => {
    const { selectedStationName, selectedYear, selectedMonth, selectedDay } = this.state;
    const isPortfolio = this.isPortfolioRequest(selectedStationName);
    if (
      selectedFiles.length === 0 ||
      !selectedStationName ||
      !selectedYear ||
      (isPortfolio && !selectedMonth)
    ) return;

    const request = {
      userId: getUserId(),
      stationName: isPortfolio ? this.getStationNames(selectedStationName) : selectedStationName,
      year: String(selectedYear),
    };
    if (selectedMonth) request.month = selectedMonth;
    if (selectedDay) request.day = selectedDay;
    const downloadContext = {
      selectedStationName, selectedYear, selectedMonth, selectedDay,
    };

    const { generate } = this.getReportApis(isPortfolio);

    this.setState({ isDownloading: true });
    generate(request)
      .then(response => {
        if (response.status !== 200) {
          this.showMessage(response.message);
          return null;
        }
        const links = (response.data && response.data.downloadDataLinks) || [];
        return this.downloadLinksAsZip(links, selectedFiles, downloadContext);
      })
      .catch(error => this.showMessage(error.message))
      .then(() => {
        if (this.componentIsMounted) this.setState({ isDownloading: false });
      });
  };

  render() {
    const { stations, t } = this.props;
    const {
      selectedStationName, selectedYear, selectedMonth, selectedDay,
      years, months, days, files, message,
      isLoadingYears, isLoadingMonths, isLoadingDays, isSubmitting, isDownloading,
      hasSubmittedRequest,
    } = this.state;
    const reportType = this.props.reportType || 'monthly';

    return (
      <div className="downloadReportPortfolio">
        <DownloadReportPortfolioSelect
          stations={stations}
          selectedStationName={selectedStationName}
          selectedYear={selectedYear}
          selectedMonth={selectedMonth}
          selectedDay={selectedDay}
          years={years}
          months={months}
          days={days}
          reportType={reportType}
          isLoadingYears={isLoadingYears}
          isLoadingMonths={isLoadingMonths}
          isLoadingDays={isLoadingDays}
          isSubmitting={isSubmitting}
          onStationChange={this.handleStationChange}
          onYearChange={this.handleYearChange}
          onMonthChange={this.handleMonthChange}
          onDayChange={this.handleDayChange}
          onSubmit={this.handleSubmit}
          onReset={this.handleReset}
        />
        <DownloadReportPortfolioTable
          files={files}
          year={selectedYear}
          month={selectedMonth}
          isPortfolio={selectedStationName === 'All'}
          isLoading={isSubmitting}
          isDownloading={isDownloading}
          hasSubmittedRequest={hasSubmittedRequest}
          onDownload={this.handleDownload}
        />
        <Snackbar
          open={Boolean(message)}
          autoHideDuration={5000}
          onClose={() => this.setState({ message: '' })}
          message={<span>{message || t('error')}</span>}
        />
      </div>
    );
  }
}

const mapStateToProps = state => ({
  stations: userStationsReducers.getStations(state),
});

export default connect(mapStateToProps)(withTranslation()(DownloadReportPortfolio));
